import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { DatePicker } from '../ui/date-picker';
import { 
  MessageSquare, 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  BarChart3,
  Download
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { 
  feedbackService, 
  type AdminFeedbackRecord, 
  type FeedbackStats, 
  type FeedbackFilters 
} from '@/client/services/feedback';
import { ExportDialog } from './export-dialog';

export function FeedbackManagement() {
  const [feedback, setFeedback] = useState<AdminFeedbackRecord[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<FeedbackFilters>({
    limit: 50,
    offset: 0
  });
  const [sortBy, setSortBy] = useState<'date' | 'participant' | 'context'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      console.log('Fetching feedback with filters:', filters);
      const response = await feedbackService.getFeedback(filters);
      console.log('Feedback response:', response);
      setFeedback(response.feedback);
      setStats(response.stats);
    } catch (error) {
      console.error('Error fetching feedback:', error);
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsProcessed = async (feedbackId: string, isProcessed: boolean) => {
    try {
      await feedbackService.markAsProcessed(feedbackId, isProcessed);
      setFeedback(prev => prev.map(item => 
        item.id === feedbackId 
          ? { ...item, isProcessed, processedAt: isProcessed ? new Date().toISOString() : undefined }
          : item
      ));
      toast.success(`Feedback ${isProcessed ? 'marked as processed' : 'unmarked'}`);
    } catch (error) {
      console.error('Error updating feedback:', error);
      toast.error('Failed to update feedback');
    }
  };

  const handleBulkProcess = async (isProcessed: boolean) => {
    if (selectedItems.size === 0) {
      toast.error('No items selected');
      return;
    }

    try {
      const feedbackIds = Array.from(selectedItems);
      await feedbackService.bulkProcess(feedbackIds, isProcessed);
      
      setFeedback(prev => prev.map(item => 
        selectedItems.has(item.id)
          ? { ...item, isProcessed, processedAt: isProcessed ? new Date().toISOString() : undefined }
          : item
      ));
      
      setSelectedItems(new Set());
      toast.success(`${feedbackIds.length} items ${isProcessed ? 'marked as processed' : 'unmarked'}`);
    } catch (error) {
      console.error('Error bulk processing feedback:', error);
      toast.error('Failed to process selected items');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(new Set(feedback.map(item => item.id)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (feedbackId: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(feedbackId);
    } else {
      newSelected.delete(feedbackId);
    }
    setSelectedItems(newSelected);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getContextBadge = (context: string) => {
    const variants = {
      demographics: 'default',
      annotation: 'secondary',
      completion: 'outline'
    } as const;
    
    return (
      <Badge variant={variants[context as keyof typeof variants] || 'default'}>
        {context}
      </Badge>
    );
  };

  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, search: value, offset: 0 }));
  };

  const handleFilterChange = (key: keyof FeedbackFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, offset: 0 }));
  };

  useEffect(() => {
    fetchFeedback();
  }, [filters]);

  if (loading && feedback.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading feedback...</div>
      </div>
    );
  }

  // Sort feedback: unprocessed first, then by selected criteria
  const sortedFeedback = [...feedback].sort((a, b) => {
    // Always prioritize unprocessed items
    if (a.isProcessed !== b.isProcessed) {
      return a.isProcessed ? 1 : -1;
    }
    
    // Then sort by selected criteria
    let comparison = 0;
    switch (sortBy) {
      case 'date':
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        break;
      case 'participant':
        const aParticipant = a.participantUsername || a.userUsername;
        const bParticipant = b.participantUsername || b.userUsername;
        comparison = aParticipant.localeCompare(bParticipant);
        break;
      case 'context':
        comparison = a.submissionContext.localeCompare(b.submissionContext);
        break;
    }
    
    return sortOrder === 'desc' ? -comparison : comparison;
  });

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Feedback</CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalCount}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Unprocessed</CardTitle>
              <XCircle className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{stats.unprocessedCount}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Processed</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.processedCount}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Length</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Math.round(stats.averageLength)}</div>
              <p className="text-xs text-muted-foreground">characters</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters & Search
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search feedback content..."
                  className="pl-8"
                  value={filters.search || ''}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={filters.processed === undefined ? 'all' : filters.processed.toString()}
                onValueChange={(value) => 
                  handleFilterChange('processed', value === 'all' ? undefined : value === 'true')
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="false">Unprocessed</SelectItem>
                  <SelectItem value="true">Processed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Context</label>
              <Select
                value={filters.context || 'all'}
                onValueChange={(value) => 
                  handleFilterChange('context', value === 'all' ? undefined : value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Contexts</SelectItem>
                  <SelectItem value="demographics">Demographics</SelectItem>
                  <SelectItem value="annotation">Annotation</SelectItem>
                  <SelectItem value="completion">Completion</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Actions</label>
              <Button 
                variant="outline" 
                onClick={() => setFilters({ limit: 50, offset: 0 })}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Date From</label>
              <DatePicker
                date={filters.dateFrom ? new Date(filters.dateFrom) : undefined}
                onSelect={(date: Date | undefined) => 
                  handleFilterChange('dateFrom', date ? date.toISOString().split('T')[0] : undefined)
                }
                placeholder="Select start date"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Date To</label>
              <DatePicker
                date={filters.dateTo ? new Date(filters.dateTo) : undefined}
                onSelect={(date: Date | undefined) => 
                  handleFilterChange('dateTo', date ? date.toISOString().split('T')[0] : undefined)
                }
                placeholder="Select end date"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Sort By</label>
              <Select
                value={sortBy}
                onValueChange={(value) => setSortBy(value as 'date' | 'participant' | 'context')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="participant">Participant</SelectItem>
                  <SelectItem value="context">Context</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Sort Order</label>
              <Select
                value={sortOrder}
                onValueChange={(value) => setSortOrder(value as 'asc' | 'desc')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Newest First</SelectItem>
                  <SelectItem value="asc">Oldest First</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedItems.size > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Mark as Processed
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Mark as Processed</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to mark {selectedItems.size} feedback item{selectedItems.size !== 1 ? 's' : ''} as processed? 
                        This will move them to the bottom of the list and mark them as reviewed.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleBulkProcess(true)}>
                        Mark as Processed
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <XCircle className="h-4 w-4 mr-1" />
                      Mark as Unprocessed
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Mark as Unprocessed</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to mark {selectedItems.size} feedback item{selectedItems.size !== 1 ? 's' : ''} as unprocessed? 
                        This will move them back to the top of the list for review.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleBulkProcess(false)}>
                        Mark as Unprocessed
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedItems(new Set())}
                >
                  Clear Selection
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Feedback List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Feedback Items</CardTitle>
              <CardDescription>
                Manage participant feedback from the annotation platform
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              <ExportDialog
                exportType="feedback"
                trigger={
                  <Button variant="outline" className="gap-2">
                    <Download className="h-4 w-4" />
                    Export Feedback
                  </Button>
                }
              />
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={selectedItems.size === feedback.length && feedback.length > 0}
                  onCheckedChange={handleSelectAll}
                />
                <span className="text-sm text-muted-foreground">Select All</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {sortedFeedback.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No feedback found</p>
              <p className="text-sm">Feedback from participants will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedFeedback.map((item) => (
                <div
                  key={item.id}
                  className={cn(
                    "border rounded-lg p-4 space-y-3 transition-all",
                    item.isProcessed && "opacity-60 bg-muted/30"
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={selectedItems.has(item.id)}
                        onCheckedChange={(checked) => handleSelectItem(item.id, checked as boolean)}
                      />
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          {getContextBadge(item.submissionContext)}
                          <span className="text-sm text-muted-foreground">
                            {formatDate(item.createdAt)}
                          </span>
                          {item.isProcessed && (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              Processed
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">{item.userUsername}</span>
                          {item.participantUsername && (
                            <span className="text-muted-foreground"> • {item.participantUsername}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          {item.isProcessed ? (
                            <>
                              <XCircle className="h-4 w-4 mr-1" />
                              Unmark
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Mark Processed
                            </>
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            {item.isProcessed ? 'Mark as Unprocessed' : 'Mark as Processed'}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {item.isProcessed 
                              ? 'This will move the feedback back to the unprocessed list for review.'
                              : 'This will mark the feedback as reviewed and move it to the bottom of the list.'
                            }
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleMarkAsProcessed(item.id, !item.isProcessed)}
                          >
                            {item.isProcessed ? 'Mark as Unprocessed' : 'Mark as Processed'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                  
                  <div className={cn(
                    "text-sm pl-8",
                    item.isProcessed && "line-through text-muted-foreground"
                  )}>
                    {item.feedbackText}
                  </div>
                  
                  <div className="flex justify-between text-xs text-muted-foreground pl-8">
                    <span>{item.characterCount} characters</span>
                    {item.processedAt && (
                      <span>Processed {formatDate(item.processedAt)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}